// "Requests need action" section on /my-day (#1600). Read-only, derived
// CLIENT-SIDE from the actor-scoped `GET /requests` list + chat-unread
// summary — no new backend endpoint. Independent of the lead-task buckets:
// its own loading/empty/error/retry, gated on `requests.read` (FR-4).
//
// PII (#1600 AC-9): the section consumes the non-PII `MyDayRequestSignalRow`
// projection only. Opening a request is delegated to the hook's
// `openRequest(id)` callback, which (since #1753) navigates to the China
// requests workspace by id — the full row is never a section prop, never
// component state, and never rendered.
import { useTranslation } from 'react-i18next';
import { ErrorState } from '../../../../ui/index.js';
import { useMyDayRequestSignals } from '../useMyDayRequestSignals.js';
import { MyDayRequestSignalRow } from './MyDayRequestSignalRow.js';

export function MyDayRequestSignalsSection() {
  const { t } = useTranslation('myDay');
  const { rows, openRequest, disabled, isLoading, isError, refetch } = useMyDayRequestSignals();

  // FR-4 / AC-5: actor without `requests.read` makes no request API call and
  //   sees no section at all; the lead-task buckets render unchanged.
  if (disabled) return null;

  return (
    <section
      data-testid="my-day-requests"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
      }}
    >
      <header style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{t('requests.title')}</h2>
        {!isError && (
          <span data-testid="my-day-requests-count" style={{ fontSize: 12, color: 'var(--fg-2)' }}>
            {rows.length}
          </span>
        )}
      </header>
      {isError ? (
        <ErrorState
          title={t('requests.error.title')}
          text={t('requests.error.text')}
          onRetry={refetch}
        />
      ) : isLoading ? (
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t('requests.loading')}</div>
      ) : rows.length === 0 ? (
        <div data-testid="my-day-requests-empty" style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          {t('requests.empty')}
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {rows.map((row) => (
            <li key={row.id}>
              <MyDayRequestSignalRow row={row} onOpen={openRequest} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
